import { Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SystemsIndex } from '@/components/sections/SystemsIndex';
import { StatusPill } from '@/components/ui/Primitives';
import { SocialIcon } from '@/components/ui/SocialIcon';
import { cx } from '@/lib/format';
import { localized, type Profile, type Project, type SocialLink } from '@/lib/types';
import { useI18n } from '@/store/preferences';

interface HeroProps {
  profile: Profile;
  socialLinks: SocialLink[];
  projects: Project[];
  technologyCount: number;
}

export function Hero({ profile, socialLinks, projects, technologyCount }: HeroProps) {
  const { t, locale } = useI18n();
  const es = locale === 'es';

  const availabilityLabel =
    profile.availability === 'OPEN_TO_WORK'
      ? t.hero.available
      : profile.availability === 'FREELANCE'
        ? t.hero.freelance
        : t.hero.notLooking;

  const resumeUrl = es ? profile.resumeEsUrl : profile.resumeEnUrl;

  const facts: [string, string][] = [
    [es ? 'Sistemas' : 'Systems', String(projects.filter((p) => p.scale === 'MAJOR').length)],
    [
      es ? 'En producción' : 'In production',
      String(projects.filter((p) => p.status === 'IN_PRODUCTION').length),
    ],
    [es ? 'Tecnologías' : 'Technologies', String(technologyCount)],
    [es ? 'Programando desde' : 'Coding since', String(profile.startedCodingYear)],
  ];

  return (
    <section className="relative pt-28 sm:pt-32">
      <div className="shell relative animate-fade-in">
        <StatusPill label={availabilityLabel} live={profile.availability !== 'NOT_LOOKING'} />

        {/* The portrait is a full-height column, not an avatar: it stretches to
            whatever the copy beside it measures, so the hero reads as one plate. */}
        <div
          className={cx(
            'mt-10 grid gap-10 sm:mt-14 lg:gap-14',
            profile.photoUrl && 'lg:grid-cols-[minmax(0,1fr)_23rem]'
          )}
        >
          <div className="flex flex-col">
            {/* A recruiter's first question is who this is, so the name is the headline. */}
            <h1 className="display text-balance">{profile.fullName}</h1>

            <p className="mt-5 text-xl font-medium text-ink sm:text-2xl">
              {localized(profile, 'headline', locale)}
            </p>
            <p className="mt-1.5 text-sm text-faint">{localized(profile, 'location', locale)}</p>

            <p className="lede mt-6 max-w-2xl text-pretty">
              {localized(profile, 'summary', locale)}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-2.5">
              <Link to="/projects" className="btn-primary">
                {t.hero.viewProjects}
              </Link>

              {resumeUrl && (
                <a href={resumeUrl} download className="btn-outline">
                  <Download className="h-3.5 w-3.5" />
                  {t.hero.downloadCv}
                </a>
              )}

              <Link to="/contact" className="btn-ghost">
                {t.hero.getInTouch}
              </Link>
            </div>

            {/* Pushed to the bottom of the column so the numbers line up with the
                foot of the portrait. `gap-px` over `bg-line` draws the hairlines. */}
            <div className="mt-10 lg:mt-auto lg:pt-12">
              <dl className="plate grid grid-cols-2 gap-px overflow-hidden bg-line sm:grid-cols-4">
                {facts.map(([label, value]) => (
                  <div key={label} className="bg-surface px-3.5 py-3">
                    <dt className="text-xs font-medium text-faint">
                      {label}
                    </dt>
                    <dd className="mt-1.5 font-mono text-sm tabular text-ink">{value}</dd>
                  </div>
                ))}
              </dl>

              <ul className="mt-3 flex items-center gap-1.5">
                {socialLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.url}
                      target={link.url.startsWith('mailto:') ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      className="btn-icon"
                      aria-label={link.label}
                    >
                      <SocialIcon iconKey={link.iconKey} className="h-4 w-4" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {profile.photoUrl && (
            <figure className="plate aspect-square overflow-hidden lg:aspect-auto">
              <img
                src={profile.photoUrl}
                alt={profile.fullName}
                width={736}
                height={736}
                className="h-full w-full object-cover"
              />
            </figure>
          )}
        </div>

        <SystemsIndex projects={projects} className="mt-14 sm:mt-16" />
      </div>
    </section>
  );
}
